import type { BlockKind, BuiltinShape, Direction } from './model';

/** Russian and English synonyms (§2.1): every word maps to one canonical meaning. */
const table = <T extends string>(entries: [T, string[]][]): Map<string, T> => {
  const m = new Map<string, T>();
  for (const [value, words] of entries) for (const w of words) m.set(w, value);
  return m;
};

export const HEADERS = table<'flow' | 'sequence'>([
  ['flow', ['схема', 'блок-схема', 'flow', 'flowchart']],
  ['sequence', ['последовательность', 'sequence']],
]);

export const DIRECTIONS = table<Direction>([
  ['down', ['вниз', 'down', 'td', 'tb']],
  ['right', ['вправо', 'right', 'lr']],
  ['up', ['вверх', 'up', 'bt']],
  ['left', ['влево', 'left', 'rl']],
]);

export const LINK_STYLES = table<'orthogonal' | 'curved'>([
  ['orthogonal', ['угловые', 'углом', 'прямоугольные', 'orthogonal', 'square']],
  ['curved', ['плавно', 'плавные', 'curved', 'smooth']],
]);

export const SHAPES = table<BuiltinShape>([
  ['rect', ['прямоугольник', 'rect', 'rectangle', 'box']],
  ['rounded', ['скруглённый', 'скругленный', 'rounded']],
  ['pill', ['капсула', 'pill', 'stadium']],
  ['circle', ['круг', 'circle']],
  ['ellipse', ['эллипс', 'ellipse', 'oval']],
  ['diamond', ['ромб', 'diamond', 'decision']],
  ['hexagon', ['шестиугольник', 'hexagon']],
  ['parallelogram', ['параллелограмм', 'parallelogram', 'io']],
  ['cylinder', ['цилиндр', 'cylinder', 'database', 'db']],
  ['document', ['документ', 'document', 'doc']],
  ['triangle', ['треугольник', 'triangle']],
  ['manual', ['ручной ввод', 'manual', 'manualinput', 'manual input']],
  ['data', ['данные', 'data']],
  ['bracket', ['скобка', 'bracket']],
  ['brace', ['фигурная скобка', 'brace']],
  ['display', ['экран', 'display']],
]);

export const STYLE_KEYS = table<'fill' | 'stroke' | 'text' | 'width' | 'dashed' | 'fine' | 'flip'>([
  ['fill', ['цвет', 'заливка', 'fill', 'bg']],
  ['stroke', ['контур', 'обводка', 'stroke', 'border']],
  ['text', ['текст', 'color', 'text']],
  ['width', ['толщина', 'width']],
  ['dashed', ['пунктир', 'dashed']],
  ['fine', ['мелкий пунктир', 'fine', 'fine-dashed', 'fine dashed']],
  ['flip', ['зеркало', 'flip', 'mirror']],
]);

/** Flowchart statements. */
export const FLOW_WORDS = table<'group' | 'class'>([
  ['group', ['группа', 'group', 'subgraph']],
  ['class', ['класс', 'class']],
]);

/** Sequence statements. */
export const SEQ_WORDS = table<'participant' | 'actor' | 'autonumber' | 'activate' | 'deactivate' | 'note'>([
  ['participant', ['участник', 'participant']],
  ['actor', ['актёр', 'актер', 'actor']],
  ['autonumber', ['нумерация', 'autonumber']],
  ['activate', ['активировать', 'activate']],
  ['deactivate', ['деактивировать', 'deactivate']],
  ['note', ['заметка', 'note']],
]);

export const BLOCKS = table<BlockKind>([
  ['loop', ['цикл', 'loop']],
  ['alt', ['если', 'alt']],
  ['opt', ['опционально', 'opt']],
  ['par', ['параллельно', 'par']],
]);

/** Words that continue a block with a new section: `} иначе ... {`. */
export const SECTION_WORDS = new Set(['иначе', 'else', 'и', 'and']);

export const NOTE_PLACES = table<'over' | 'left' | 'right'>([
  ['over', ['над', 'over']],
  ['left', ['слева', 'left']],
  ['right', ['справа', 'right']],
]);

/** Filler words after a note place: `слева от`, `left of`. */
export const NOTE_FILLERS = new Set(['от', 'of']);
